import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RegisterItemList } from '../RegisterItemList';
import type { Item } from '../RegisterItemList';
import { initiativesApi } from '../../services/trackerApi';
import type { Initiative } from '../../types/trackerModels';
import type { MeetingMinutes, MinuteEntry, MinutesStatus } from '../../types/models';
import { meetingMinutesApi, peopleApi } from '../../services/api';
import {
  MINUTES_STATUSES,
  MINUTES_STATUS_LABELS,
  MINUTES_STATUS_TONE,
  MINUTE_CATEGORY_ICON,
} from '../../constants/meetingMinutes';
import { formatDate } from '../../utils/format';
import { exportMeetingMinutesPdf } from '../../utils/meetingMinutesPdf';
import { useToast } from '../../hooks/useToast';

/** Tile icon per lifecycle status — the stat row above the table. */
const MINUTES_STATUS_ICONS: Record<MinutesStatus, string> = {
  draft: 'ri-draft-line',
  circulated: 'ri-send-plane-line',
  approved: 'ri-verified-badge-line',
};

const Dash: React.FC = () => <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;

/** Status is a stored code (draft/circulated/approved), so map it to label + tone. */
const statusChip = (status: string): React.ReactNode => {
  const key = status as MinutesStatus;
  const label = MINUTES_STATUS_LABELS[key] || status;
  const tone = MINUTES_STATUS_TONE[key] || 'neutral';
  return <span className={`np-chip np-tone-${tone}`}>{label}</span>;
};

/** `entries` arrives untyped off the generic register item — narrow it once, here. */
const entriesOf = (item: Item): MinuteEntry[] =>
  Array.isArray(item.entries) ? (item.entries as MinuteEntry[]) : [];

/**
 * Actions still outstanding. Only 'action' entries carry a meaningful status, so
 * every other category is ignored; anything not explicitly 'done' counts as open
 * (a legacy record with a missing status should nag rather than silently pass).
 */
const openActionCount = (item: Item): number =>
  entriesOf(item).filter((e) => e.category === 'action' && e.status !== 'done').length;

export const MeetingMinutesList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<MinutesStatus | ''>('');
  // Defaults to hidden, same as every other register's approach to a settled/inactive state -
  // archived minutes still exist and are still exportable, they just don't clutter the
  // everyday view. Session-only (resets on reload), matching RegisterItemList's own
  // hiddenStatuses toggle rather than inventing a persisted-preference mechanism.
  const [showArchived, setShowArchived] = useState(false);
  const [exporting, setExporting] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  // Minutes store only the initiativeId; the tracker owns the display name.
  // Shares the ['initiatives'] cache key with the rest of the tracker pages.
  const { data: initiatives = [] } = useQuery<Initiative[]>({
    queryKey: ['initiatives'],
    queryFn: initiativesApi.getAll,
  });

  const initiativeNames = useMemo(() => {
    const map = new Map<string, string>();
    initiatives.forEach((i) => map.set(i.initiativeId, i.name));
    return map;
  }, [initiatives]);

  const initiativeLabel = (item: Item): string => {
    const id = String(item.initiativeId || '');
    if (!id) return '';
    return initiativeNames.get(id) || id;
  };

  /**
   * Register-level export: one PDF holding every set of minutes the status filter admits,
   * oldest meeting first, each starting on a fresh page.
   *
   * Worth having rather than only the per-record button because the thing auditors and
   * steering groups ask for is a *pack* — "the approved minutes for this period" — and with
   * the status filter set to Approved that is exactly what this produces.
   *
   * Two honest limits. It follows the status filter (this page owns it) but not the search
   * box or the hidden-status popover, which live inside RegisterItemList and are not exposed
   * to headerActions; the tooltip says so. And the records are fetched on click rather than
   * with the page — the table's own rows come from RegisterItemList's generic list query, so
   * loading a second typed copy of every record plus the people directory up front would
   * cost two requests on every visit for a button that is pressed occasionally.
   */
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const [all, people] = await Promise.all([
        queryClient.fetchQuery<MeetingMinutes[]>({
          queryKey: ['meetingMinutes'],
          queryFn: () => meetingMinutesApi.list(),
        }),
        queryClient.fetchQuery({ queryKey: ['people'], queryFn: peopleApi.getAll }),
      ]);

      const chosen = all
        .filter((m) => !statusFilter || m.status === statusFilter)
        .filter((m) => showArchived || !m.archivedAt)
        // Chronological: a pack of minutes reads as a timeline, not as a register listing.
        .sort((a, b) => (a.meetingDate || '').localeCompare(b.meetingDate || ''));

      if (chosen.length === 0) {
        toast.error(
          statusFilter
            ? `There are no ${MINUTES_STATUS_LABELS[statusFilter].toLowerCase()} minutes to export.`
            : 'There are no minutes to export.'
        );
        return;
      }

      exportMeetingMinutesPdf(chosen, people, initiatives);
      toast.success(`Exported ${chosen.length} ${chosen.length === 1 ? 'meeting' : 'meetings'} to PDF.`);
    } catch (err) {
      toast.error(
        `Could not generate the PDF: ${err instanceof Error ? err.message : 'unknown error'}`
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <RegisterItemList
      headerActions={
        <button
          type="button"
          className="np-btn np-btn-ghost"
          onClick={() => void handleExport()}
          disabled={exporting}
          title={
            statusFilter
              ? `Export every ${MINUTES_STATUS_LABELS[statusFilter].toLowerCase()} set of minutes as one PDF, oldest meeting first`
              : 'Export every set of minutes as one PDF, oldest meeting first. The status filter narrows it; the search box does not.'
          }
        >
          <i className="ri-file-pdf-2-line"></i>
          {exporting ? 'Generating…' : 'Export PDF'}
        </button>
      }
      title="Meeting Minutes"
      iconClass="ri-file-list-3-line"
      listPath="/meeting-minutes"
      detailPathPrefix="/meeting-minutes"
      idField="minutesId"
      titleField="title"
      statusField="status"
      statusChip={statusChip}
      statCards={(items) => [
        ...MINUTES_STATUSES.map((s) => ({
          label: MINUTES_STATUS_LABELS[s],
          value: items.filter((item) => item.status === s).length,
          icon: MINUTES_STATUS_ICONS[s],
          tone: MINUTES_STATUS_TONE[s],
        })),
        {
          label: 'Open actions',
          value: items.reduce((total, item) => total + openActionCount(item), 0),
          icon: MINUTE_CATEGORY_ICON.action,
          tone: 'warning',
        },
        {
          // Total count regardless of the showArchived toggle, matching every other tile
          // here (they always reflect the whole register, not the current row filter).
          label: 'Archived',
          value: items.filter((item) => Boolean(item.archivedAt)).length,
          icon: 'ri-archive-line',
          tone: 'neutral',
        },
      ]}
      extraFilters={[
        {
          id: 'minutes-status',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 220 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as MinutesStatus | '')}
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              {MINUTES_STATUSES.map((s) => (
                <option key={s} value={s}>{MINUTES_STATUS_LABELS[s]}</option>
              ))}
            </select>
          ),
          predicate: (item) => !statusFilter || item.status === statusFilter,
        },
        {
          id: 'minutes-show-archived',
          control: (
            <label className="d-flex align-items-center gap-2" style={{ fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          ),
          predicate: (item) => showArchived || !item.archivedAt,
        },
      ]}
      extraColumns={[
        {
          header: 'Date',
          width: '130px',
          render: (item) =>
            item.meetingDate
              ? <span className="np-tabular">{formatDate(String(item.meetingDate))}</span>
              : <Dash />,
          // Raw YYYY-MM-DD sorts chronologically as plain text.
          sortValue: (item) => String(item.meetingDate || ''),
        },
        {
          header: 'Initiative',
          width: '220px',
          render: (item) => {
            const label = initiativeLabel(item);
            return label ? <span className="np-sub">{label}</span> : <Dash />;
          },
          sortValue: (item) => initiativeLabel(item),
        },
        {
          header: 'Entries',
          width: '150px',
          render: (item) => {
            const count = entriesOf(item).length;
            if (count === 0) return <Dash />;
            const open = openActionCount(item);
            return (
              <span className="d-flex align-items-center gap-2">
                <span className="np-tabular">{count}</span>
                {open > 0 && (
                  <span
                    className="np-chip np-tone-warning"
                    title={`${open} open ${open === 1 ? 'action' : 'actions'}`}
                  >
                    <i className={MINUTE_CATEGORY_ICON.action}></i>{open}
                  </span>
                )}
              </span>
            );
          },
          sortValue: (item) => entriesOf(item).length,
        },
      ]}
    />
  );
};

export default MeetingMinutesList;
